import * as ExtensionType from './types.impl.js';
import { ExtensionData, ComponentType } from './types.impl.js';

/**
 * Create a widget iframe that users can display on their site. We recommend submitting a proposal before using iframes in your app.
 * Learn More: https://devforum.wix.com/en/article/widget-components
 * @param id - A unique identifier for the extension
 * @param data - An iframe to be displayed on the user’s site
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function widget({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WidgetComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIDGET,
    compData: { widgetComponentData: data },
  };
}

/**
 * Display an iframe as a page on a user's site.
 * Learn More: https://dev.wix.com/docs/build-apps/developer-tools/extensions/iframes/guide-to-page-extensions
 * @param id - A unique identifier for the extension
 * @param data - An iframe to be displayed as a full page on the user’s site
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function page({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PageComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PAGE,
    compData: { pageComponentData: data },
  };
}

/**
 * Load a WOOI directly in the Editor DOM.
 * Learn More: https://dev.wix.com/docs/fed-guild/articles/viewer/platform/intro/get-started/guidelines
 * @param id - A unique identifier for the extension
 * @param data - A widget out of iframe component that loads directly in the Editor DOM
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function widgetOutOfIframe({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WidgetOutOfIframeComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIDGET_OUT_OF_IFRAME,
    compData: { widgetOutOfIframeData: data },
  };
}

/**
 * Load a POOI directly in the Editor DOM.
 * Learn More: https://dev.wix.com/docs/fed-guild/articles/viewer/platform/intro/get-started/guidelines
 * @param id - A unique identifier for the extension
 * @param data - A page out of iframe component that loads directly in the Editor DOM
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function pageOutOfIframe({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PageOutOfIframeComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PAGE_OUT_OF_IFRAME,
    compData: { pageOutOfIframeData: data },
  };
}

/**
 * Load a collection of native Editor components directly in the Editor DOM.
 * Learn More: https://dev.wix.com/docs/fed-guild/articles/editor-platform/welcome-to-the-editor-platform
 * @param id - A unique identifier for the extension
 * @param data - Create a collection of native Editor components that loads directly in the Editor DOM
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function platform({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PlatfromComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PLATFORM,
    compData: { platformComponentData: data },
  };
}

/**
 * Open an iframe in the Dashboard, or add a link to a page on your platform.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/dashboard-extensions/external-links/about-external-link-extensions
 * @param id - A unique identifier for the extension
 * @param data - An iframe that opens in the user’s Wix Dashboard,
 * or add a link to open the page directly in your platform.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function dashboard({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DashboardComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.DASHBOARD,
    compData: { dashboardComponentData: data },
  };
}

/**
 * Use an invisible iframe to track activity. It’s added to every page on the site (max. 1).
 * Learn More: https://devforum.wix.com/en/article/worker-components
 * @param id - A unique identifier for the extension
 * @param data - An invisible iframe to track site activity. It’ll be added to every page on the user’s site
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function worker({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WorkerComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WORKER,
    compData: { workerComponentData: data },
  };
}

/**
 * Use Wix Blocks to create a widget component. Wix Blocks will open in a new tab.
 * Learn More: https://bo.wix.com/wix-docs/rest/drafts/wix-blocks-server---app-builder
 * @param id - A unique identifier for the extension
 * @param data - An extension to platform used by Wix Blocks UI
 * The main component in the Wix Blocks.
 * no manual adding. auto-created only from Wix Blocks
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function studio({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.StudioComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.STUDIO,
    compData: { studioComponentData: data },
  };
}

/**
 * A widget component in the Wix Blocks.
 * Learn More: https://bo.wix.com/wix-docs/rnd/dev-center/components-catalog/wix-blocks
 * @param id - A unique identifier for the extension
 * @param data - A widget component in the Wix Blocks.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function studioWidget({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.StudioWidgetComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.STUDIO_WIDGET,
    compData: { studioWidgetComponentData: data },
  };
}

/**
 * A component that indicates the existence of an importable code package in a Wix Blocks application.
 * Learn More: https://bo.wix.com/wix-docs/rnd/dev-center/components-catalog/wix-blocks-code-package
 * @param id - A unique identifier for the extension
 * @param data - A component that indicates the existence of an importable code package in a Wix Blocks application.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function codePackage({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CodePackageComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CODE_PACKAGE,
    compData: { codePackageComponentData: data },
  };
}

/**
 * Load a collection of native components directly in the Business Manager.
 * Learn More: https://bo.wix.com/wix-docs/rnd/dev-center/components-catalog/dashboard-platfrom/dashboard-platfrom-component-data-object
 * @param id - A unique identifier for the extension
 * @param data - A collection of native components that load directly in the Business Manager
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function dashboardPlatform({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DashboardPlatfromComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.DASHBOARD_PLATFORM,
    compData: { dashboardPlatformComponentData: data },
  };
}

/**
 * Inject a script into a site’s HTML code.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/site-extensions/embedded-scripts/about-embedded-scripts
 * @param id - A unique identifier for the extension
 * @param data - Inject third party scripts into the user’s site
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function embeddedScript({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EmbeddedScriptComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EMBEDDED_SCRIPT,
    compData: { embeddedScriptComponentData: data },
  };
}

/**
 * Display a draggable component in the Editor using an HTML custom element.
 * Learn More: https://devforum.wix.com/kb/en/article/create-a-custom-element
 * @param id - A unique identifier for the extension
 * @param data - A draggable custom element. Add your custom script and generate ui directly in the viewer
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function web({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WebComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WEB,
    compData: { webComponentData: data },
  };
}

/**
 * Extend app with internal Wix extension.
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function extension({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ExtensionData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EXTENSION,
    compData: { extensionData: data },
  };
}

/** @internal
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function snippetSolution({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SnippetSolutionData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SNIPPET_SOLUTION,
    compData: { snippetSolutionsData: data },
  };
}

/**
 * Extend a site’s CMS to store and manage custom data.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/data-collections/about-data-collections-extensions
 * @param id - A unique identifier for the extension
 * @param data - An extension for adding data collections to a site when the app is installed.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function dataComponent({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DataComponent;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.DATA_COMPONENT,
    compData: { dataComponent: data },
  };
}

/** @internal
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function dcConfig({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DCConfigData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.DC_CONFIG,
    compData: { dcConfigData: data },
  };
}

/**
 * Serve a file from the user’s site domain.
 * Learn More: https://devforum.wix.com/en/article/serving-staic-files-from-the-installed-sites-domain
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function staticFile({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.StaticFileComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.STATIC_FILE,
    compData: { staticFileComponentData: data },
  };
}

/** @internal
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function appConfig({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AppConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.APP_CONFIG,
    compData: { appConfig: data },
  };
}

/**
 * Create a multi-page dashboard in the Business Manager.
 * Learn More: https://bo.wix.com/wix-docs/rnd/dev-center/components-catalog/multiple-dashboards
 * @param id - A unique identifier for the extension
 * @param data - Business Manager sidebar category that contains sidebar links.
 * each link in the category opens in the business manager in an iframe.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function multipleDashboards({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.MultipleDashboardsComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.MULTIPLE_DASHBOARDS,
    compData: { multipleDashboardsComponentData: data },
  };
}

/**
 * Payments gateway extension description
 * @param id - A unique identifier for the extension
 * @param data - Test component extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function paymentsGateway({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PaymentsGatewayComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PAYMENTS_GATEWAY,
    compData: { paymentsGateway: data },
  };
}

/**
 * Extend an app with automation capabilities using CRM Automations
 * Learn More: https://bo.wix.com/wix-docs/rnd/dev-center/components-catalog/automation-trigger
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function automationTrigger({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AutomationTrigger;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.AUTOMATION_TRIGGER,
    compData: { automationTrigger: data },
  };
}

/**
 * Any restrictions on invoices actions
 * Learn More: https://bo.wix.com/wix-docs/rnd/dev-center/components-catalog/invoices-actions
 * @param id - A unique identifier for the extension
 * @param data - represents Invoices integration policies
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function invoicesActions({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.InvoicesActionsComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.INVOICES_ACTIONS,
    compData: { invoicesActionsComponentData: data },
  };
}

/**
 * Experimental-WIP: Business manager app_config, configures a module and its page components in business manager.
 * Learn More: https://bo.wix.com/wix-docs/rnd/dev-center/components-catalog/dashboard-application
 * @param id - A unique identifier for the extension
 * @param data - Experimental-WIP: Specifies the app module configuration of business manager module
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function dashboardApplication({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DashboardApplicationData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.DASHBOARD_APPLICATION,
    compData: { dashboardApplicationData: data },
  };
}

/**
 * Contact Labels Components
 * Learn More: https://bo.wix.com/wix-docs/rnd/dev-center/components-catalog/contact-labels
 * @param id - A unique identifier for the extension
 * @param data - Contact Labels Extensions
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function contactLabels({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ContactLabelsComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CONTACT_LABELS,
    compData: { contactLabels: data },
  };
}

/**
 * Display a widget that fits into a predefined slot in a Wix app.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/site-extensions/site-plugins/about-site-plugin-extensions
 * @param id - A unique identifier for the extension
 * @param data - A component to be rendered within Widget Slots
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function widgetPlugin({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WidgetPluginComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIDGET_PLUGIN,
    compData: { widgetPlugin: data },
  };
}

/**
 * Cross Sell Integration
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function crossSell({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CrossSellConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CROSS_SELL,
    compData: { crossSell: data },
  };
}

/**
 * Experimental-WIP: Local Delivery extension description.
 * Learn More: https://bo.wix.com/wix-docs/rnd/dev-center/components-catalog/local-delivery
 * @param id - A unique identifier for the extension
 * @param data - Local Delivery component
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function localDelivery({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.LocalDeliveryComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.LOCAL_DELIVERY,
    compData: { localDelivery: data },
  };
}

/**
 * WIP: Velo payment provider SPI
 * @param id - A unique identifier for the extension
 * @param data - Configuration for the payment service SPI implementor
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function paymentProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PaymentServiceProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PAYMENT_PROVIDER,
    compData: { paymentProvider: data },
  };
}

/**
 * Memberships SPI for ECOM platform
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomMemberships({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.MembershipsSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_MEMBERSHIPS,
    compData: { ecomMemberships: data },
  };
}

/**
 * Line Items Enricher SPI for ECOM platform
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomLineItemsEnricher({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.LineItemsEnricherConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_LINE_ITEMS_ENRICHER,
    compData: { ecomLineItemsEnricher: data },
  };
}

/**
 * Provide custom shipping rates to a site's cart and checkout.
 * Learn More: https://dev.wix.com/docs/rest/business-solutions/e-commerce/service-plugins/shipping-rates-integration-service-plugin/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function ecomShippingRates({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ShippingRatesConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_SHIPPING_RATES,
    compData: { ecomShippingRates: data },
  };
}

/**
 * Shipping Label Carrier SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function shippingLabelCarrier({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ShippingLabelCarrierSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SHIPPING_LABEL_CARRIER,
    compData: { shippingLabelCarrier: data },
  };
}

/**
 * Restaurants POS service plugin
 * @param id - A unique identifier for the extension
 * @param data - Restaurants POS component
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function restaurantsPos({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.RestaurantsPOSComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.RESTAURANTS_POS,
    compData: { restaurantsPos: data },
  };
}

/**
 * A fictional Shipping SPI that is used by developers to complete SPI training
 * https://github.com/wix-academy/spi-in-scala-path
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function fictionalShippingProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ShippingProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FICTIONAL_SHIPPING_PROVIDER,
    compData: { fictionalShippingProvider: data },
  };
}

/**
 * Alert Enricher Integration
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function alertEnricher({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AlertEnricherSpiConfiguration;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ALERT_ENRICHER,
    compData: { alertEnricher: data },
  };
}

/**
 * Extend a Wix entity with custom fields for your app
 * @param id - A unique identifier for the extension
 * @param data - A component holding schema of Data Extensions
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function dataExtensions({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DataExtensionsComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.DATA_EXTENSIONS,
    compData: { dataExtensions: data },
  };
}

/**
 * A common component type for all Generic Hooks
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function genericHooks({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.GenericHooksConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.GENERIC_HOOKS,
    compData: { genericHooks: data },
  };
}

/**
 * WIP: automations action provider
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function automationsActionProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ActionProviderSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.AUTOMATIONS_ACTION_PROVIDER,
    compData: { automationsActionProvider: data },
  };
}

/**
 * Become a Wix catalog provider and integrate any external repository of sellable items with the Wix eCommerce platform.
 * Learn More: https://dev.wix.com/docs/rest/business-solutions/e-commerce/catalog-service-plugin/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function ecomCatalog({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CatalogSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_CATALOG,
    compData: { ecomCatalog: data },
  };
}

/**
 * Add a space to a Wix Dashboard page that can be extended with a widget or menu item.
 * @param id - A unique identifier for the extension
 * @param data - A container (slot) that can be extended by other applications (e.g. widget slot or context menu slot)
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function backOfficeExtensionContainer({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficeExtensionContainer;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_EXTENSION_CONTAINER,
    compData: { backOfficeExtensionContainer: data },
  };
}

/**
 * Create a widget or menu item that will be rendered within a Wix Dashboard page.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/dashboard-extensions/dashboard-plugins/about-dashboard-plugin-extensions
 * @param id - A unique identifier for the extension
 * @param data - A component that enables extending Wix Dashboard Functionality, as defined by ExtendingComponentType.
 * Currently this supports extending menus and extending widget slots (for Wix apps that expose such menus and slots)
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function backOfficeExtension({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficeExtension;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_EXTENSION,
    compData: { backOfficeExtension: data },
  };
}

/**
 * WIP: automations trigger provider
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function automationsTriggerProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.TriggerProviderSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.AUTOMATIONS_TRIGGER_PROVIDER,
    compData: { automationsTriggerProvider: data },
  };
}

/**
 * WIP - SPI for execution before identity creation in the new IAM platform
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function identityPreRegistration({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PreRegisterConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.IDENTITY_PRE_REGISTRATION,
    compData: { identityPreRegistration: data },
  };
}

/**
 * Premium products upgrades/downgrades
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function premiumProductsPaths({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ProductsPathsConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PREMIUM_PRODUCTS_PATHS,
    compData: { premiumProductsPaths: data },
  };
}

/**
 * ECOM custom scopes provider - define scopes of catalog items based custom attributes
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomCustomScope({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CustomScopeConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_CUSTOM_SCOPE,
    compData: { ecomCustomScope: data },
  };
}

/**
 * Implement gift card benefits such as balance retrieval, redemption, and voiding transactions.
 * Learn More: https://dev.wix.com/docs/rest/business-solutions/e-commerce/service-plugins/gift-cards-service-plugin/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function giftCardsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.GiftCardProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.GIFT_CARDS_PROVIDER,
    compData: { giftCardsProvider: data },
  };
}

/**
 * Dealer External Filter SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function dealerExternalFilterProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ExternalFilterProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.DEALER_EXTERNAL_FILTER_PROVIDER,
    compData: { dealerExternalFilterProvider: data },
  };
}

/**
 * Recommendations provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomRecommendationsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.RecommendationsProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_RECOMMENDATIONS_PROVIDER,
    compData: { ecomRecommendationsProvider: data },
  };
}

/**
 * Dropshipping Provider SPI for Ecom Platform
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomDropshippingProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DropshippingProviderSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_DROPSHIPPING_PROVIDER,
    compData: { ecomDropshippingProvider: data },
  };
}

/**
 * Invoices SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function invoicesProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.InvoicesConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.INVOICES_PROVIDER,
    compData: { invoicesProvider: data },
  };
}

/**
 * Provide SEO keyword suggestions to site collaborators, report quota usage, and provide a page where users can upgrade their plan.
 * Learn More: https://dev.wix.com/docs/rest/api-reference/marketing/seo-keywords-suggestions-v-1/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function seoKeywordsSuggestions({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SeoKeywordsSuggestionsSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SEO_KEYWORDS_SUGGESTIONS,
    compData: { seoKeywordsSuggestions: data },
  };
}

/**
 * Ecom custom discount trigger service plugin - define custom triggers to apply discount rules
 * Learn More: https://dev.wix.com/docs/rest/business-solutions/e-commerce/service-plugins/custom-discount-triggers-integration-service-plugin/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomDiscountsTrigger({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CustomTriggerConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_DISCOUNTS_TRIGGER,
    compData: { ecomDiscountsTrigger: data },
  };
}

/**
 * Multilingual content provider for site translator to read and update translations
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function multilingualContentProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ContentProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.MULTILINGUAL_CONTENT_PROVIDER,
    compData: { multilingualContentProvider: data },
  };
}

/**
 * Application Automation
 * @param id - A unique identifier for the extension
 * @param data - Automation Component data that is stored in dev center
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function applicationAutomation({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ApplicationAutomationComponent;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.APPLICATION_AUTOMATION,
    compData: { applicationAutomation: data },
  };
}

/**
 * Experimental-WIP: A back office page category
 * @param id - A unique identifier for the extension
 * @param data - Back-office sidebar categories
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function backOfficeSidebarCategory({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficeSidebarCategory;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_SIDEBAR_CATEGORY,
    compData: { backOfficeSidebarCategory: data },
  };
}

/**
 * Configure a page for your app that will be embedded in the Wix Dashboard.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/dashboard-extensions/dashboard-pages/about-dashboard-page-extensions
 * @param id - A unique identifier for the extension
 * @param data - A page in one of the back-office platforms
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function backOfficePage({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficePage;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_PAGE,
    compData: { backOfficePage: data },
  };
}

/**
 * Provide custom additional fees that are added to a site's cart and checkout.
 * Learn More: https://dev.wix.com/docs/rest/business-solutions/e-commerce/additional-fees-service-plugin/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function ecomAdditionalFees({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AdditionalFeesSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_ADDITIONAL_FEES,
    compData: { ecomAdditionalFees: data },
  };
}

/**
 * Send notifications to your app users
 * @param id - A unique identifier for the extension
 * @param data - Describes a notification that can be sent to users
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function pingUserNotification({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PingNotificationComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PING_USER_NOTIFICATION,
    compData: { pingUserNotification: data },
  };
}

/**
 * Bookings Pricing Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function bookingsPricingProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BookingsPricingProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BOOKINGS_PRICING_PROVIDER,
    compData: { bookingsPricingProvider: data },
  };
}

/**
 * Authenticator SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function identityAuthenticator({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AuthenticatorConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.IDENTITY_AUTHENTICATOR,
    compData: { identityAuthenticator: data },
  };
}

/**
 * WIP - SPI for authenticating via external IDP in the new IAM platform
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function identityIdpConnector({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.IDPConnectionConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.IDENTITY_IDP_CONNECTOR,
    compData: { identityIdpConnector: data },
  };
}

/**
 * Items Selector
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function itemsSelectionProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ItemsSelectionProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ITEMS_SELECTION_PROVIDER,
    compData: { itemsSelectionProvider: data },
  };
}

/**
 * Portfolio synced projects provider
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function portfolioSyncedProjectsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SyncedProjectsProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PORTFOLIO_SYNCED_PROJECTS_PROVIDER,
    compData: { portfolioSyncedProjectsProvider: data },
  };
}

/**
 * Communication Channel
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function communicationChannel({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CommunicationChannelConfiguration;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.COMMUNICATION_CHANNEL,
    compData: { communicationChannel: data },
  };
}

/**
 * WIP - SPI to run after the login process
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function identityPostLogin({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PostLoginConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.IDENTITY_POST_LOGIN,
    compData: { identityPostLogin: data },
  };
}

/**
 * Create a component that can be easily placed in or opened from a Wix app page.
 * @param id - A unique identifier for the extension
 * @param data - A general widget in one of the back-office platforms
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function backOfficeWidget({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficeWidget;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_WIDGET,
    compData: { backOfficeWidget: data },
  };
}

/**
 * Social Marketing Design SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function socialMarketingDesign({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SocialMarketingDesignSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SOCIAL_MARKETING_DESIGN,
    compData: { socialMarketingDesign: data },
  };
}

/**
 * Forms Submissions SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function formsSubmissionsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FormSubmissionSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FORMS_SUBMISSIONS_PROVIDER,
    compData: { formsSubmissionsProvider: data },
  };
}

/**
 * Wix Offering
 * @param id - A unique identifier for the extension
 * @param data - Redirects to either a page component in the Business Manager or to a wix answers article
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixOffering({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WixOfferingComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIX_OFFERING,
    compData: { wixOffering: data },
  };
}

/**
 * Components translation test component
 * @param id - A unique identifier for the extension
 * @param data - A component used for testing dev center functionality
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function devCenterTestingComponent({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DevCenterTestingComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.DEV_CENTER_TESTING_COMPONENT,
    compData: { devCenterTestingComponent: data },
  };
}

/**
 * Components Validator SPI
 * @param id - A unique identifier for the extension
 * @param data - SPI config - defines the relation between implementer URL and which types it is validates
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function componentsValidatorProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ComponentsValidatorConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.COMPONENTS_VALIDATOR_PROVIDER,
    compData: { componentsValidatorProvider: data },
  };
}

/**
 * Components Translations additional fields config
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function componentsTranslationsAdditionalFieldsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ComponentTranslationAdditionalFieldsConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.COMPONENTS_TRANSLATIONS_ADDITIONAL_FIELDS_PROVIDER,
    compData: { componentsTranslationsAdditionalFieldsProvider: data },
  };
}

/**
 * Forms Schema SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function formsSchemaProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FormSchemaSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FORMS_SCHEMA_PROVIDER,
    compData: { formsSchemaProvider: data },
  };
}

/**
 * Bookings External Calendar Provider
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function bookingsExternalCalendarProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BOOKINGS_EXTERNAL_CALENDAR_PROVIDER,
    compData: { bookingsExternalCalendarProvider: data },
  };
}

/**
 * Default Tax Category Config
 * Learn More: https://bo.wix.com/wix-docs/rest/drafts/platform-tax
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomDefaultTaxationCategory({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DefaultTaxGroupProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_DEFAULT_TAXATION_CATEGORY,
    compData: { ecomDefaultTaxationCategory: data },
  };
}

/**
 * Viewer Dynamic Site Structure Config
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function viewerDynamicSiteStructureProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DynamicSiteStructureProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.VIEWER_DYNAMIC_SITE_STRUCTURE_PROVIDER,
    compData: { viewerDynamicSiteStructureProvider: data },
  };
}

/**
 * Send notifications to your app users of users
 * @param id - A unique identifier for the extension
 * @param data - Describes a notification that can be sent to users
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function pingUouNotification({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PingNotificationComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PING_UOU_NOTIFICATION,
    compData: { pingUouNotification: data },
  };
}

/**
 * Holds data for authenticating Headless Applications
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function headlessOauth({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.HeadlessOAuth;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.HEADLESS_OAUTH,
    compData: { headlessOauth: data },
  };
}

/**
 * Tax Calculator Structure Config
 * Learn More: https://bo.wix.com/wix-docs/rest/drafts/platform-tax
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomTaxCalculatorSpi({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.TaxCalculatorSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_TAX_CALCULATOR_SPI,
    compData: { ecomTaxCalculatorSpi: data },
  };
}

/**
 * Comments Moderation Provider Config
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function commentsModerationProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CommentModerationProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.COMMENTS_MODERATION_PROVIDER,
    compData: { commentsModerationProvider: data },
  };
}

/**
 * GridApp files transformer config
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function gridAppFilesTransformer({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.GridAppFilesTransformerConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.GRID_APP_FILES_TRANSFORMER,
    compData: { gridAppFilesTransformer: data },
  };
}

/**
 * Holds benefit programs policy provider configuration
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function benefitProgramsPolicyProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PolicyConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BENEFIT_PROGRAMS_POLICY_PROVIDER,
    compData: { benefitProgramsPolicyProvider: data },
  };
}

/**
 * Apply custom charges to a site based on usage of your app's services.
 * Learn More: https://dev.wix.com/docs/rest/api-reference/app-management/apps/custom-charges-spi/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function premiumCustomCharges({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CustomChargesConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PREMIUM_CUSTOM_CHARGES,
    compData: { premiumCustomCharges: data },
  };
}

/**
 * Validate items in a site's cart and checkout based on custom logic.
 * Learn More: https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/validations-integration-spi/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function ecomValidations({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ValidationsSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_VALIDATIONS,
    compData: { ecomValidations: data },
  };
}

/**
 * Component Reference Data SPI
 * @param id - A unique identifier for the extension
 * @param data - SPI config - defines the relation between implementer URL and which types it saves
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function componentReferenceDataProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ComponentReferenceDataConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.COMPONENT_REFERENCE_DATA_PROVIDER,
    compData: { componentReferenceDataProvider: data },
  };
}

/**
 * Wix Reviews SPI
 * @param id - A unique identifier for the extension
 * @param data - Configuration for ReviewsProductCatalogProvider
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixReviewsProductCatalog({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ReviewsProductCatalogProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIX_REVIEWS_PRODUCT_CATALOG,
    compData: { wixReviewsProductCatalog: data },
  };
}

/**
 * Promote Social Marketing Designs Provider Configuration
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function socialMarketingDesignsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SocialMarketingDesignsProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SOCIAL_MARKETING_DESIGNS_PROVIDER,
    compData: { socialMarketingDesignsProvider: data },
  };
}

/**
 * Promote Google Business Profile Provider Configuration
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function googleBusinessProfileFeatureProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.GbpFeatureConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.GOOGLE_BUSINESS_PROFILE_FEATURE_PROVIDER,
    compData: { googleBusinessProfileFeatureProvider: data },
  };
}

/**
 * Comments Filter Provider Config
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function commentsFilterProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CommentFilterProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.COMMENTS_FILTER_PROVIDER,
    compData: { commentsFilterProvider: data },
  };
}

/**
 * Tax ID Validator SPI Config
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function billingTaxIdValidator({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.TaxIdValidatorConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BILLING_TAX_ID_VALIDATOR,
    compData: { billingTaxIdValidator: data },
  };
}

/**
 * Ping Settings Group Data
 * @param id - A unique identifier for the extension
 * @param data - Describes a setting group of a notification
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function pingSettingsGroup({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PingSettingsGroupComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PING_SETTINGS_GROUP,
    compData: { pingSettingsGroup: data },
  };
}

/**
 * Forms Spam Submissions SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function formsSpamSubmissionsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FormSpamSubmissionSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FORMS_SPAM_SUBMISSIONS_PROVIDER,
    compData: { formsSpamSubmissionsProvider: data },
  };
}

/**
 * Build a tool that adds functionality to the Wix editors.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/editor-extensions/about-editor-extensions
 * @param id - A unique identifier for the extension
 * @param data - Editor Add-on component
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function editorAddon({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EditorAddon;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EDITOR_ADDON,
    compData: { editorAddon: data },
  };
}

/**
 * External Database component
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function externalDatabaseProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ExternalDatabaseSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EXTERNAL_DATABASE_PROVIDER,
    compData: { externalDatabaseProvider: data },
  };
}

/**
 * Integrate your service with Wix's payment process to allow merchants to request and use your services on their Wix sites.
 * Learn More: https://dev.wix.com/docs/rest/business-solutions/e-commerce/service-plugins/payment-settings-integration-service-plugin/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function ecomPaymentSettings({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PaymentSettingsSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_PAYMENT_SETTINGS,
    compData: { ecomPaymentSettings: data },
  };
}

/**
 * Group multiple notifications into one preference toggle.
 * Learn More: https://dev.wix.com/docs/build-apps/build-your-app/user-notifications/add-notification-topics
 * @param id - A unique identifier for the extension
 * @param data - Describes topic of a notification
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function notificationTopic({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.NotificationTopic;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.NOTIFICATION_TOPIC,
    compData: { notificationTopic: data },
  };
}

/**
 * Notification Preference SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function notificationPreferencesFilterProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.NotificationPreferencesFilterConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.NOTIFICATION_PREFERENCES_FILTER_PROVIDER,
    compData: { notificationPreferencesFilterProvider: data },
  };
}

/**
 * Bookings Resource Types provider SPI config
 * @param id - A unique identifier for the extension
 * @param data - This config provides details of the resource types that are managed by an app implementing this component SPI.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function bookingsResourceTypesProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BookingsResourceTypesProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BOOKINGS_RESOURCE_TYPES_PROVIDER,
    compData: { bookingsResourceTypesProvider: data },
  };
}

/**
 * Pricing Plans form configuration
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function pricingPlansFormConfiguration({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PricingPlansFormConfiguration;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PRICING_PLANS_FORM_CONFIGURATION,
    compData: { pricingPlansFormConfiguration: data },
  };
}

/**
 * Create a notification to alert your app users about important updates and events.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/notifications/about-notification-extensions
 * @param id - A unique identifier for the extension
 * @param data - Describes user notifications
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function userNotification({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.UserNotification;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.USER_NOTIFICATION,
    compData: { userNotification: data },
  };
}

/**
 * Contact Notification
 * @param id - A unique identifier for the extension
 * @param data - Describes contact notifications
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function contactNotification({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ContactNotification;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CONTACT_NOTIFICATION,
    compData: { contactNotification: data },
  };
}

/**
 * Add a site page that displays selected widgets.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/site-extensions/site-pages/about-site-page-extensions
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function unifiedPage({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.UnifiedPage;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.UNIFIED_PAGE,
    compData: { unifiedPage: data },
  };
}

/**
 * Availability Time Slots Configuration Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function availabilityTimeSlotsConfigurationProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AvailabilityTimeSlotsProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.AVAILABILITY_TIME_SLOTS_CONFIGURATION_PROVIDER,
    compData: { availabilityTimeSlotsConfigurationProvider: data },
  };
}

/**
 * Proposal Editor Provider SPI config
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function proposalEditorProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ProposalEditorProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PROPOSAL_EDITOR_PROVIDER,
    compData: { proposalEditorProvider: data },
  };
}

/**
 * Custom Table Reservations Provider SPI config
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function customTableReservationsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CustomReservationsApprovalConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CUSTOM_TABLE_RESERVATIONS_PROVIDER,
    compData: { customTableReservationsProvider: data },
  };
}

/**
 * Comments Context provider
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function commentsContextProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CommentsContextProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.COMMENTS_CONTEXT_PROVIDER,
    compData: { commentsContextProvider: data },
  };
}

/**
 * Forms Spam Submission Reports SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function formsSpamSubmissionReportsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FormSpamSubmissionReportSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FORMS_SPAM_SUBMISSION_REPORTS_PROVIDER,
    compData: { formsSpamSubmissionReportsProvider: data },
  };
}

/**
 * Automation Velo Action Spi Provider
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function automationsVeloActionProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.VeloActionConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.AUTOMATIONS_VELO_ACTION_PROVIDER,
    compData: { automationsVeloActionProvider: data },
  };
}

/**
 * Calendar Event Type Provider
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function calendarEventTypeProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EventTypeProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CALENDAR_EVENT_TYPE_PROVIDER,
    compData: { calendarEventTypeProvider: data },
  };
}

/**
 * Reserved - previously was `service_availability_policy_provider`
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function reserved({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ServiceAvailabilityPolicyProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.RESERVED,
    compData: { reserved: data },
  };
}

/**
 * Create an SMS message for a pre-installed automation, in order to translate it.
 * @param id - A unique identifier for the extension
 * @param data - a translated sms text sent to UOU
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function smsActionMessage({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SmsActionMessage;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SMS_ACTION_MESSAGE,
    compData: { smsActionMessage: data },
  };
}

/**
 * Booking Policy Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function bookingPolicyProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BookingPolicyProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BOOKING_POLICY_PROVIDER,
    compData: { bookingPolicyProvider: data },
  };
}

/**
 * Multi Service Booking Policy Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function multiServiceBookingPolicyProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.MultiServiceBookingPolicyProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.MULTI_SERVICE_BOOKING_POLICY_PROVIDER,
    compData: { multiServiceBookingPolicyProvider: data },
  };
}

/**
 * Forms Submissions Extension SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function formsSubmissionsExtensionProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FormSubmissionSpiExtensionConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FORMS_SUBMISSIONS_EXTENSION_PROVIDER,
    compData: { formsSubmissionsExtensionProvider: data },
  };
}

/**
 * AI Assistant SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function aiAssistant({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AssistantSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.AI_ASSISTANT,
    compData: { aiAssistant: data },
  };
}

/**
 * Multilingual translation schema
 * Learn More: https://dev.wix.com/docs/api-reference/business-management/multilingual/translation/translation-schema/schema-object
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function multilingualTranslationSchema({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.MultilingualTranslationSchema;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.MULTILINGUAL_TRANSLATION_SCHEMA,
    compData: { multilingualTranslationSchema: data },
  };
}

/**
 * Tax Groups Provider Service Plugin
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function taxGroupsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.TaxGroupsProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.TAX_GROUPS_PROVIDER,
    compData: { taxGroupsProvider: data },
  };
}

/**
 * Tax Calculation Provider Service Plugin
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function taxCalculationProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.TaxCalculationConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.TAX_CALCULATION_PROVIDER,
    compData: { taxCalculationProvider: data },
  };
}

/**
 * Create a reusable modal that can be utilized across multiple pages within your app and in other applications.
 * @param id - A unique identifier for the extension
 * @param data - A modal in one of the back-office platforms
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function backOfficeModal({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficeModal;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_MODAL,
    compData: { backOfficeModal: data },
  };
}

/**
 * Deployment Pipeline Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function deploymentPipelineProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DeploymentPipelineProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.DEPLOYMENT_PIPELINE_PROVIDER,
    compData: { deploymentPipelineProvider: data },
  };
}

/**
 * Display a draggable widget on a site using a self-hosted custom element.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/site-extensions/site-widgets/about-site-widget-extensions
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function customElementWidget({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CustomElementWidget;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CUSTOM_ELEMENT_WIDGET,
    compData: { customElementWidget: data },
  };
}

/**
 * Create a widget that will be rendered within a Wix Dashboard page.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/dashboard-extensions/dashboard-plugins/about-dashboard-plugin-extensions
 * @param id - A unique identifier for the extension
 * @param data - A component that enables extending Wix dashboard functionality.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function backOfficeExtensionWidget({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficeExtensionWidget;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_EXTENSION_WIDGET,
    compData: { backOfficeExtensionWidget: data },
  };
}

/**
 * Create a menu item that will be rendered within a Wix Dashboard page.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/dashboard-extensions/dashboard-plugins/dashboard-menu-plugins/dashboard-menu-plugin-extensions
 * @param id - A unique identifier for the extension
 * @param data - A component that enables extending a Wix dashboard menu.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function backOfficeExtensionMenuItem({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficeExtensionMenuItem;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_EXTENSION_MENU_ITEM,
    compData: { backOfficeExtensionMenuItem: data },
  };
}

/**
 * Form Template
 * @param id - A unique identifier for the extension
 * @param data - Form templates represent an entity that can be used as a source when creating a new form,
 * as well as used as common, extendable form.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function formTemplate({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FormTemplate;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FORM_TEMPLATE,
    compData: { formTemplate: data },
  };
}

/**
 * Notification Content
 * @param id - A unique identifier for the extension
 * @param data - Describes a setting group of a notification
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function notificationContent({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.NotificationContent;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.NOTIFICATION_CONTENT,
    compData: { notificationContent: data },
  };
}

/**
 * Broadcast List
 * @param id - A unique identifier for the extension
 * @param data - Describes topic of a notification
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function broadcastList({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BroadcastList;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BROADCAST_LIST,
    compData: { broadcastList: data },
  };
}

/**
 * Partners Payouts
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function partnersPayouts({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PayoutsProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PARTNERS_PAYOUTS,
    compData: { partnersPayouts: data },
  };
}

/**
 * Wix Reviews Entity Catalog Provider SPI
 * @param id - A unique identifier for the extension
 * @param data - Configuration for ReviewsEntityCatalogProvider
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixReviewsEntityCatalog({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ReviewsEntityCatalogProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIX_REVIEWS_ENTITY_CATALOG,
    compData: { wixReviewsEntityCatalog: data },
  };
}

/**
 * Velo Publish Pipeline Task Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function veloPublishPipelineTaskProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.VeloPublishPipelineTaskProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.VELO_PUBLISH_PIPELINE_TASK_PROVIDER,
    compData: { veloPublishPipelineTaskProvider: data },
  };
}

/**
 * Function shop POC SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function functionsShopPriceProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FunctionsShopPriceSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FUNCTIONS_SHOP_PRICE_PROVIDER,
    compData: { functionsShopPriceProvider: data },
  };
}

/**
 * Connect your service plugins to Functions so users can define their own custom logic.
 * Learn More: https://github.com/wix-private/functions-client/blob/master/README.md
 * @param id - A unique identifier for the extension
 * @param data - A component that describes a Function
 * Design is highly influenced by business schema api
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function functionExtension({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType._Function;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FUNCTION,
    compData: { function: data },
  };
}

/**
 * Ecom Checkout Content
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomCheckoutContent({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CheckoutContentSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_CHECKOUT_CONTENT,
    compData: { ecomCheckoutContent: data },
  };
}

/**
 * Communication Channel
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function communicationChannelProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.V2CommunicationChannelConfiguration;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.COMMUNICATION_CHANNEL_PROVIDER,
    compData: { communicationChannelProvider: data },
  };
}

/**
 * Webhook
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function webhook({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WebhookComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WEBHOOK,
    compData: { webhook: data },
  };
}

/**
 * An extension that holds topology data.
 * @param id - A unique identifier for the extension
 * @param data - A component that holds topology data for an application.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function topology({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.TopologyComponentData;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.TOPOLOGY,
    compData: { topology: data },
  };
}

/**
 * Loyalty Custom Rewards POC
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function loyaltyCustomRewards({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CustomRewardProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.LOYALTY_CUSTOM_REWARDS,
    compData: { loyaltyCustomRewards: data },
  };
}

/**
 * Create a predefined template that users can use when creating flows in Functions.
 * Learn More: https://github.com/wix-private/functions-client/blob/master/docs/function-templates.md
 * @param id - A unique identifier for the extension
 * @param data - Wix Users can choose to create a "Function" component according to predefined recipes for smoother user experience
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function functionRecipe({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FunctionRecipe;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FUNCTION_RECIPE,
    compData: { functionRecipe: data },
  };
}

/**
 * external url to the back office
 * @param id - A unique identifier for the extension
 * @param data - A backoffice component for external url`s
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function backOfficeExternalUrl({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficeExternalUrl;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_EXTERNAL_URL,
    compData: { backOfficeExternalUrl: data },
  };
}

/**
 * Authentication factor
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function identityFactor({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FactorConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.IDENTITY_FACTOR,
    compData: { identityFactor: data },
  };
}

/**
 * ecom discounts
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomDiscounts({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DiscountsSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_DISCOUNTS,
    compData: { ecomDiscounts: data },
  };
}

/**
 * Blocks Velo custom css - for CSS editing in Blocks
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function veloCustomCss({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.VeloCustomCss;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.VELO_CUSTOM_CSS,
    compData: { veloCustomCss: data },
  };
}

/**
 * Dealer Adaptive Component Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function dealerAdaptiveComponentProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AdaptiveComponentProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.DEALER_ADAPTIVE_COMPONENT_PROVIDER,
    compData: { dealerAdaptiveComponentProvider: data },
  };
}

/**
 * Wix AI Assistant Action SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function aiAssistantAction({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ActionSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.AI_ASSISTANT_ACTION,
    compData: { aiAssistantAction: data },
  };
}

/**
 * Integrate any external inventory with the Wix eCommerce platform.
 * Learn More: https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/catalogs/inventory-service-plugin/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function ecomInventory({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.InventorySpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_INVENTORY,
    compData: { ecomInventory: data },
  };
}

/**
 * Add a monitoring configuration to your app.
 * Learn More: https://dev.wix.com/docs/build-apps/manage-your-app/observability/monitor-your-app-with-sentry
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function monitoring({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.Monitoring;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.MONITORING,
    compData: { monitoring: data },
  };
}

/**
 * PAPI SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function papiProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PluginConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PAPI_PROVIDER,
    compData: { papiProvider: data },
  };
}

/**
 * Add a lightbox that displays a widget in a popup over a site page.
 * Learn More: https://dev.wix.com/docs/build-apps/develop-your-app/extensions/site-extensions/site-lightboxes/about-site-lightbox-extensions
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function unifiedLightbox({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.UnifiedLightbox;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.UNIFIED_LIGHTBOX,
    compData: { unifiedLightbox: data },
  };
}

/**
 * Forms Schema Extension SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function formsExtensionProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FormSpiExtensionConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FORMS_EXTENSION_PROVIDER,
    compData: { formsExtensionProvider: data },
  };
}

/**
 * Multilingual translation schema group
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function multilingualTranslationSchemaGroup({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SchemaGroup;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.MULTILINGUAL_TRANSLATION_SCHEMA_GROUP,
    compData: { multilingualTranslationSchemaGroup: data },
  };
}

/**
 * Panel data
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function panel({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.Panel;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PANEL,
    compData: { panel: data },
  };
}

/**
 * Premium Purchase Validations SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function premiumPurchaseValidations({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PurchaseValidationsConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PREMIUM_PURCHASE_VALIDATIONS,
    compData: { premiumPurchaseValidations: data },
  };
}

/**
 * Any Cookies, localStorage, sessionStorage or IndexDB items the app is using in the site or their domain
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function browserStorage({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BrowserStorage;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BROWSER_STORAGE,
    compData: { browserStorage: data },
  };
}

/**
 * Map this app to SDK module
 * @param id - A unique identifier for the extension
 * @param data - Describe SDK module definition for an app
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function sdkDefinition({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SdkDefinition;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SDK_DEFINITION,
    compData: { sdkDefinition: data },
  };
}

/**
 * Add a space to a Page or Widget that can be extended with a widget plugin.
 * @param id - A unique identifier for the extension
 * @param data - A container (slot) that can be extended by other applications (e.g. widget plugin)
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function siteWidgetSlot({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SiteWidgetSlot;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SITE_WIDGET_SLOT,
    compData: { siteWidgetSlot: data },
  };
}

/**
 * Calendar Event Validation Provider
 * @param id - A unique identifier for the extension
 * @param data - Configuration for the event validation provider.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function calendarEventValidationProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EventValidationProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CALENDAR_EVENT_VALIDATION_PROVIDER,
    compData: { calendarEventValidationProvider: data },
  };
}

/** @internal
 * @param id - A unique identifier for the extension
 * @param data - Client Service is a broad category encompassing any value, function, or feature that an application needs. A service is typically a class with a narrow, well-defined purpose. A component is one type of class that can use DI.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function clientSideService({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ClientSideService;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CLIENT_SIDE_SERVICE,
    compData: { clientSideService: data },
  };
}

/**
 * Billing Settings SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function billingSettings({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BillingSettingsConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BILLING_SETTINGS,
    compData: { billingSettings: data },
  };
}

/**
 * Patterns Wizard
 * @param id - A unique identifier for the extension
 * @param data - A component holding configuration of Patterns Wizard
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function patternsWizard({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PatternsWizard;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PATTERNS_WIZARD,
    compData: { patternsWizard: data },
  };
}

/**
 * Application Profile SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function applicationProfile({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ApplicationProfileProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.APPLICATION_PROFILE,
    compData: { applicationProfile: data },
  };
}

/**
 * Create an TextToSpeech message for a pre-installed automation, in order to translate it.
 * @param id - A unique identifier for the extension
 * @param data - a translated TextToSpeech payload sent to UOU
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function textToSpeechActionMessage({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.TextToSpeechActionMessage;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.TEXT_TO_SPEECH_ACTION_MESSAGE,
    compData: { textToSpeechActionMessage: data },
  };
}

/**
 * Audience Provider
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function audienceProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AudienceProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.AUDIENCE_PROVIDER,
    compData: { audienceProvider: data },
  };
}

/**
 * Pricing Plans Price
 * @param id - A unique identifier for the extension
 * @param data - Config for the Price SPI
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function pricingPlansPrice({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PriceSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PRICING_PLANS_PRICE,
    compData: { pricingPlansPrice: data },
  };
}

/**
 * Pricing Plans Start Date Limits SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function pricingPlanStartDateLimits({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.StartDateLimitsSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PRICING_PLAN_START_DATE_LIMITS,
    compData: { pricingPlanStartDateLimits: data },
  };
}

/**
 * Pricing Plans Start Date Rules SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function pricingPlanStartDateRules({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.StartDateRulesSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PRICING_PLAN_START_DATE_RULES,
    compData: { pricingPlanStartDateRules: data },
  };
}

/**
 * Events ticket reservations SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function eventsTicketReservations({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.TicketReservationsSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EVENTS_TICKET_RESERVATIONS,
    compData: { eventsTicketReservations: data },
  };
}

/**
 * WIP: Dispute Service Plugin
 * @param id - A unique identifier for the extension
 * @param data - Configuration for the dispute service plugin implementor
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function paymentsDisputeServicePlugin({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.DisputeServicePluginConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PAYMENTS_DISPUTE_SERVICE_PLUGIN,
    compData: { paymentsDisputeServicePlugin: data },
  };
}

/**
 * Pricing Plans Fees
 * @param id - A unique identifier for the extension
 * @param data - Config for the Fees SPI
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function pricingPlansFees({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FeesSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PRICING_PLANS_FEES,
    compData: { pricingPlansFees: data },
  };
}

/**
 * A react component with native and custom editor abilities
 * @param id - A unique identifier for the extension
 * @param data - The full model for an imported component set in DEV Center
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function editorReactComponent({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EditorReactComponent;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EDITOR_REACT_COMPONENT,
    compData: { editorReactComponent: data },
  };
}

/** @internal
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function suppliersHubMarketplace({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.MarketplaceSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SUPPLIERS_HUB_MARKETPLACE,
    compData: { suppliersHubMarketplace: data },
  };
}

/**
 * Form Schema Dynamic Values
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function formSchemaDynamicValues({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FormSchemaDynamicValuesSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FORM_SCHEMA_DYNAMIC_VALUES,
    compData: { formSchemaDynamicValues: data },
  };
}

/**
 * Blog paywall SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function blogPaywallProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BlogPaywallProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BLOG_PAYWALL_PROVIDER,
    compData: { blogPaywallProvider: data },
  };
}

/**
 * Loyalty Custom Rewards V2
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function loyaltyCustomRewardsV2({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.RewardProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.LOYALTY_CUSTOM_REWARDS_V2,
    compData: { loyaltyCustomRewardsV2: data },
  };
}

/**
 * Restriction service plugin for editing products managed by third-party fulfillment apps, such as dropshipping or print-on-demand services
 * Learn More: https://dev.wix.com/docs/rest/business-solutions/stores/service-plugins/product-restrictions-v3/introduction
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function storesProductRestrictions({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ProductRestrictionsConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.STORES_PRODUCT_RESTRICTIONS,
    compData: { storesProductRestrictions: data },
  };
}

/**
 * Form Submission Moderation SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function formSubmissionModeration({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FormSubmissionModerationSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FORM_SUBMISSION_MODERATION,
    compData: { formSubmissionModeration: data },
  };
}

/**
 * Event badges SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function eventsEventBadges({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EventBadgesSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EVENTS_EVENT_BADGES,
    compData: { eventsEventBadges: data },
  };
}

/**
 * Billing Operation SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function billingOperation({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.OperationExecutorConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BILLING_OPERATION,
    compData: { billingOperation: data },
  };
}

/**
 * customization to the back office
 * @param id - A unique identifier for the extension
 * @param data - Back-office Customization
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function backOfficeCustomization({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficeCustomization;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_CUSTOMIZATION,
    compData: { backOfficeCustomization: data },
  };
}

/**
 * Dev Center components enricher SPI
 * @param id - A unique identifier for the extension
 * @param data - SPI config - defines the relation between implementer URL and which types it validates
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function componentEnricherProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ComponentEnricherConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.COMPONENT_ENRICHER_PROVIDER,
    compData: { componentEnricherProvider: data },
  };
}

/**
 * Restricted customization to the back office
 * @param id - A unique identifier for the extension
 * @param data - Back-office Restricted Customization
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function backOfficeRestrictedCustomization({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackOfficeRestrictedCustomization;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACK_OFFICE_RESTRICTED_CUSTOMIZATION,
    compData: { backOfficeRestrictedCustomization: data },
  };
}

/**
 * Editor app previews POC
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function editorAppPreviewsPoc({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AppPreviewProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EDITOR_APP_PREVIEWS_POC,
    compData: { editorAppPreviewsPoc: data },
  };
}

/**
 * Legends Persona Configuration SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function legendsPersonaConfiguration({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.LegendsSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.LEGENDS_PERSONA_CONFIGURATION,
    compData: { legendsPersonaConfiguration: data },
  };
}

/**
 * Wix Hosting App Deployment Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixHostingAppDeploymentProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AppDeploymentProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIX_HOSTING_APP_DEPLOYMENT_PROVIDER,
    compData: { wixHostingAppDeploymentProvider: data },
  };
}

/**
 * A component to store the Velo BaaS deployment data
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function backendWorker({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BackendWorker;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BACKEND_WORKER,
    compData: { backendWorker: data },
  };
}

/**
 * Event Time Slots Configuration Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function eventTimeSlotsConfigurationProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EventTimeSlotsProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EVENT_TIME_SLOTS_CONFIGURATION_PROVIDER,
    compData: { eventTimeSlotsConfigurationProvider: data },
  };
}

/**
 * Wix Hosting App Environment Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixHostingAppEnvironmentProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AppEnvironmentProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIX_HOSTING_APP_ENVIRONMENT_PROVIDER,
    compData: { wixHostingAppEnvironmentProvider: data },
  };
}

/**
 * SDK exports for existing app relavent SDK
 * @param id - A unique identifier for the extension
 * @param data - SDK exports specify the metadata of a module.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function sdkExports({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SDKExports;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SDK_EXPORTS,
    compData: { sdkExports: data },
  };
}

/**
 * A Javascript service for Apps and React Components that will be run in the viewer (client and server side rendering) and in the editor
 * @param id - A unique identifier for the extension
 * @param data - A service to be used by components and apps in the viewer and editor
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function viewerService({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ViewerService;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.VIEWER_SERVICE,
    compData: { viewerService: data },
  };
}

/**
 * Wix Payments Provider Account Service Plugin
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixPaymentsProviderAccountServicePlugin({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ProviderAccountServicePluginConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIX_PAYMENTS_PROVIDER_ACCOUNT_SERVICE_PLUGIN,
    compData: { wixPaymentsProviderAccountServicePlugin: data },
  };
}

/**
 * contacts segments v2 filter provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function contactsSegmentsV2FilterProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ProviderConfigMessage;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CONTACTS_SEGMENTS_V2_FILTER_PROVIDER,
    compData: { contactsSegmentsV2FilterProvider: data },
  };
}

/**
 * Wix Analytics product catalog provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function analyticsProductCatalog({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ProductCatalogProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ANALYTICS_PRODUCT_CATALOG,
    compData: { analyticsProductCatalog: data },
  };
}

/**
 * Wix Partner Store SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function siteMigration({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SiteMigrationSpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SITE_MIGRATION,
    compData: { siteMigration: data },
  };
}

/**
 * Restaurnts POS Provider Settings SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function restaurantsPosProviderSettingsServicePlugin({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PosProviderSettingsServicePluginConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.RESTAURANTS_POS_PROVIDER_SETTINGS_SERVICE_PLUGIN,
    compData: { restaurantsPosProviderSettingsServicePlugin: data },
  };
}

/**
 * Wix Vibe instructions
 * @param id - A unique identifier for the extension
 * @param data - A component that represents a set of instructions for Wix Vibe to execute on app installation
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixVibeCodingInstructions({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WixVibeCodingInstructions;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIX_VIBE_CODING_INSTRUCTIONS,
    compData: { wixVibeCodingInstructions: data },
  };
}

/**
 * Tax Exempt Groups Provider Service Plugin
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function taxExemptGroupsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.TaxExemptGroupsProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.TAX_EXEMPT_GROUPS_PROVIDER,
    compData: { taxExemptGroupsProvider: data },
  };
}

/**
 * Bookings Automations Configuration Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function bookingAutomationsConfiguration({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BookingAutomationsConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BOOKING_AUTOMATIONS_CONFIGURATION,
    compData: { bookingAutomationsConfiguration: data },
  };
}

/**
 * Bookings Platform Configuration Component
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function bookingsPlatformConfiguration({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BookingsPlatformConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BOOKINGS_PLATFORM_CONFIGURATION,
    compData: { bookingsPlatformConfiguration: data },
  };
}

/**
 * Bookings Validation Provider SPI - validates bookings for external providers
 * @param id - A unique identifier for the extension
 * @param data - Configuration for the bookings validation provider.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function bookingsValidationProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BookingsValidationProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BOOKINGS_VALIDATION_PROVIDER,
    compData: { bookingsValidationProvider: data },
  };
}

/**
 * Wix Vibe UI Component
 * @param id - A unique identifier for the extension
 * @param data - A component that represents a Wix Vibe UI component editing configuration
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixVibeComponent({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WixVibeComponent;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIX_VIBE_COMPONENT,
    compData: { wixVibeComponent: data },
  };
}

/**
 * Wixel SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixel({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WixelSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIXEL,
    compData: { wixel: data },
  };
}

/**
 * Bookings Staff Sorting Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function staffSortingProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.StaffSortingProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.STAFF_SORTING_PROVIDER,
    compData: { staffSortingProvider: data },
  };
}

/**
 * Function Ecom Discount Trigger Eligibility Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function functionEcomDiscountTriggerEligibilityProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FunctionEcomDiscountTriggerEligibilityProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FUNCTION_ECOM_DISCOUNT_TRIGGER_ELIGIBILITY_PROVIDER,
    compData: { functionEcomDiscountTriggerEligibilityProvider: data },
  };
}

/**
 * Functions Assistant Tools SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function functionsAssistantTools({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FunctionAssistantToolProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.FUNCTIONS_ASSISTANT_TOOLS,
    compData: { functionsAssistantTools: data },
  };
}

/**
 * Chunked Site Map Provider SPI for apps that serve large amount of site map entries
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function siteMapInfoProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.SiteMapProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SITE_MAP_INFO_PROVIDER,
    compData: { siteMapInfoProvider: data },
  };
}

/** @internal
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function wixAiGatewayCallback({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.WixAiGatewaySpiConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIX_AI_GATEWAY_CALLBACK,
    compData: { wixAiGatewayCallback: data },
  };
}

/**
 * URL mapper provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function urlMapperProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.UrlMapperProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.URL_MAPPER_PROVIDER,
    compData: { urlMapperProvider: data },
  };
}

/**
 * Provider suppression plugin
 * @param id - A unique identifier for the extension
 * @param data - A ProviderSuppressionServicePluginConfig is entity which holds dev center app configuration for allowed payment provider on site
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function providerSuppressionServicePlugin({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ProviderSuppressionServicePluginConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PROVIDER_SUPPRESSION_SERVICE_PLUGIN,
    compData: { providerSuppressionServicePlugin: data },
  };
}

/**
 * collect form data for checkout items
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function ecomForms({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.FormsSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ECOM_FORMS,
    compData: { ecomForms: data },
  };
}

/**
 * Editor Context Provider
 * @param id - A unique identifier for the extension
 * @param data - A component that provides context to children and can be added
 * to any page, container or section in the Editor.
 * The compoent is logical and does not provide any UI.
 * It does however provide props for configuring it in the Editor.
 * The bundles can export the following:
 * method `default` export: the context provider component
 * method `injectAccessTokenGetter`:  A way to have an access token injected
 * hooks: The hook method exposed for usage (`useSomeContextName`)
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function editorContextProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EditorContextProvider;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EDITOR_CONTEXT_PROVIDER,
    compData: { editorContextProvider: data },
  };
}

/**
 * Editor Function Library
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function editorFunctionLibrary({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EditorFunctionLibrary;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EDITOR_FUNCTION_LIBRARY,
    compData: { editorFunctionLibrary: data },
  };
}

/**
 * Use routers to reuse pages design across multiple data items with dynamic URLs
 * @param id - A unique identifier for the extension
 * @param data - Configuration for an app-level router that serves multiple pages.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function appRouter({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AppRouter;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.APP_ROUTER,
    compData: { appRouter: data },
  };
}

/**
 * Wixel Binding SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixelBinding({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BindingsSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIXEL_BINDING,
    compData: { wixelBinding: data },
  };
}

/**
 * Suppliers Hub Marketplace Provider V2
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function shubMarketplaceProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.MarketplaceProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SHUB_MARKETPLACE_PROVIDER,
    compData: { shubMarketplaceProvider: data },
  };
}

/**
 * Booking Action URLs Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function bookingActionUrlsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BookingActionUrlsProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BOOKING_ACTION_URLS_PROVIDER,
    compData: { bookingActionUrlsProvider: data },
  };
}

/**
 * Calendar Conferencing Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function calendarConferencingProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ConferencingProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CALENDAR_CONFERENCING_PROVIDER,
    compData: { calendarConferencingProvider: data },
  };
}

/**
 * Validate cancel action provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function bassValidateCancelAction({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CancelSubscriptionsConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BASS_VALIDATE_CANCEL_ACTION,
    compData: { bassValidateCancelAction: data },
  };
}

/**
 * Wixel viewer router provider SPI
 * @param id - A unique identifier for the extension
 * @param data - Configuration for a viewer router SPI implementation.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function wixelViewerRouterProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ViewerRouterSPIConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.WIXEL_VIEWER_ROUTER_PROVIDER,
    compData: { wixelViewerRouterProvider: data },
  };
}

/**
 * Premium Action SPI
 * @param id - A unique identifier for the extension
 * @param data - Premium action SPI configuration
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function premiumAction({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PremiumActionConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.PREMIUM_ACTION,
    compData: { premiumAction: data },
  };
}

/**
 * Automation Template
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function automationTemplate({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AutomationTemplateComponent;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.AUTOMATION_TEMPLATE,
    compData: { automationTemplate: data },
  };
}

/**
 * Agent Maker Approval Provider SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function agentMakerApprovalProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ApprovalProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.AGENT_MAKER_APPROVAL_PROVIDER,
    compData: { agentMakerApprovalProvider: data },
  };
}

/**
 * Service Tags Configuration SPI - declares valid tags for bookings services
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function serviceTagsConfiguration({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ServiceTagsConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.SERVICE_TAGS_CONFIGURATION,
    compData: { serviceTagsConfiguration: data },
  };
}

/**
 * Loyalty Points Adjustment SPI - allows Functions to adjust loyalty points before assignment
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function loyaltyPointsAdjustment({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.PointsAdjustmentProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.LOYALTY_POINTS_ADJUSTMENT,
    compData: { loyaltyPointsAdjustment: data },
  };
}

/**
 * Loyalty Earn Points Limit SPI
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function loyaltyEarnPointsLimit({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EarnPointsLimitProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.LOYALTY_EARN_POINTS_LIMIT,
    compData: { loyaltyEarnPointsLimit: data },
  };
}

/**
 * Realtime Permissions Provider SPI - integrate with Wix to control who can receive messages on your Wix Realtime channels, by providing a custom permissions check that authorizes whether a subscriber is allowed to subscribe to your app's channels.
 * Learn More: https://dev.wix.com/docs/sdk/core-modules/realtime/extensions/realtime-permissions-provider/check-subscriber-permissions
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
export function realtimePermissionsProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.RealtimePermissionsProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.REALTIME_PERMISSIONS_PROVIDER,
    compData: { realtimePermissionsProvider: data },
  };
}

/**
 * Calendar Search Provider SPI — allows entities to participate in Calendar search
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function calendarSearchProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.CalendarSearchProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CALENDAR_SEARCH_PROVIDER,
    compData: { calendarSearchProvider: data },
  };
}

/**
 * EditorInstallation — declares what to install when the parent app is installed in the editor
 * @param id - A unique identifier for the extension
 * @param data - Declares what to install when the parent app is installed in the editor.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function editorInstallation({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EditorInstallation;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EDITOR_INSTALLATION,
    compData: { editorInstallation: data },
  };
}

/**
 * App-level actions surfaced in the App Manager menu.
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function appActions({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.AppActions;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.APP_ACTIONS,
    compData: { appActions: data },
  };
}

/**
 * Rollout Step Executor SPI — declares per-implementer config for services that execute one step of a multi-site rollout on one site.
 * @param id - A unique identifier for the extension
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function rolloutStepExecutor({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.RolloutStepExecutorConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.ROLLOUT_STEP_EXECUTOR,
    compData: { rolloutStepExecutor: data },
  };
}

/**
 * Bookings Platform Premium Validation SPI configuration.
 * @param id - A unique identifier for the extension
 * @param data - Install-time configuration each provider registers with the host.
 * Determines endpoint routing, operation scope, and host behavior when
 * the provider is unavailable.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function bookingsPlatformPremiumValidation({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.BookingsPlatformPremiumValidationProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.BOOKINGS_PLATFORM_PREMIUM_VALIDATION,
    compData: { bookingsPlatformPremiumValidation: data },
  };
}

/**
 * Global CSS — raw CSS content for the site.
 * @param id - A unique identifier for the extension
 * @param data - Global CSS — carries the site's raw CSS content.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function globalCss({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.GlobalCssConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.GLOBAL_CSS,
    compData: { globalCss: data },
  };
}

/**
 * Connector Config — declares how the app connects to a third-party provider, including its identity and authentication method.
 * @param id - A unique identifier for the extension
 * @param data - Declares how the app connects to a third-party provider (e.g. Google, Slack, GitHub),
 * including the provider identity and the authentication method used to access it.
 * Client id/secret are NOT stored here — they are resolved at runtime from the app's secret.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function connectorConfig({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ConnectorConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CONNECTOR_CONFIG,
    compData: { connectorConfig: data },
  };
}

/**
 * Channel Data Provider SPI — lets a channel integrator provide business data (name, type, address) for a connected channel account.
 * @param id - A unique identifier for the extension
 * @param data - Configuration for a ChannelDataProvider implementer, stored on the host side.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function channelDataProvider({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.ChannelDataProviderConfig;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.CHANNEL_DATA_PROVIDER,
    compData: { channelDataProvider: data },
  };
}

/**
 * Per-client OAuth configuration consumed by the Wix OAuth2 authorization server (e.g. a custom sign-in page).
 * @param id - A unique identifier for the extension
 * @param data - Per-client OAuth configuration consumed by the Wix OAuth2 authorization server.
 *
 * Lets any client (not only headless OAuth apps) override OAuth-flow settings such as the
 * sign-in page the user is sent to during authorization. Unset values fall back to the Wix defaults.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function oauthClientConfiguration({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.OAuthClientConfiguration;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.OAUTH_CLIENT_CONFIGURATION,
    compData: { oauthClientConfiguration: data },
  };
}

/**
 * EditorExposure — declares the items an app exposes in editor panels.
 * @param id - A unique identifier for the extension
 * @param data - Declares the items an app exposes in editor panels.
 * @param name - Optional - A human readable name for the extension
 * @returns A general form of extensions
 */
/** @internal */
export function editorExposure({
  id,
  data,
  name,
}: {
  id: string;
  data: ExtensionType.EditorExposure;
  name?: string;
}): ExtensionData {
  return {
    compId: id,
    compName: name,
    compType: ComponentType.EDITOR_EXPOSURE,
    compData: { editorExposure: data },
  };
}
